import { createContext, useContext, useCallback, useState, ReactNode } from "react";
import { workOrdersService } from "@/services";
import { WorkOrderDto } from "@shared/types/WorkOrderDto";

interface WorkOrdersContextType {
  selectWorkOrder: (serviceOrderId: string) => Promise<WorkOrderDto>;
  selectWorkOrderByInvoiceId: (invoiceId: string) => Promise<WorkOrderDto>;
  getWorkOrderByServiceOrder: (serviceOrderId: string) => Promise<WorkOrderDto | null>;
  workOrdersMap: Record<string, WorkOrderDto | null>;
  isLoadingWorkOrders: boolean;
}

const WorkOrdersContext = createContext<WorkOrdersContextType | null>(null);

export function WorkOrdersProvider({ children }: { children: ReactNode }) {
  const [workOrdersMap, setWorkOrdersMap] = useState<Record<string, WorkOrderDto | null>>({});
  const [isLoadingWorkOrders, setIsLoadingWorkOrders] = useState(false);

  const selectWorkOrder = useCallback(async (serviceOrderId: string) => {
    try {
      const data = await workOrdersService.selectWorkOrder({
        serviceOrderId,
      });
      return data;
    } catch (err) {
      throw new Error("Failed to select work order");
    }
  }, []);

  const selectWorkOrderByInvoiceId = useCallback(async (invoiceId: string) => {
    try {
      const data = await workOrdersService.selectWorkOrder({
        invoiceId,
      });
      return data;
    } catch (err) {
      throw new Error("Failed to select work order by invoice id");
    }
  }, []);

  const getWorkOrderByServiceOrder = useCallback(async (serviceOrderId: string) => {
    setIsLoadingWorkOrders(true);
    try {
      const data = await workOrdersService.getWorkOrderByServiceOrder(serviceOrderId);
      setWorkOrdersMap((prev) => ({ ...prev, [serviceOrderId]: data }));
      return data;
    } catch (err) {
      throw new Error("Failed to fetch work order for service order");
    } finally {
      setIsLoadingWorkOrders(false);
    }
  }, []);

  return (
    <WorkOrdersContext.Provider
      value={{
        selectWorkOrder,
        selectWorkOrderByInvoiceId,
        getWorkOrderByServiceOrder,
        workOrdersMap,
        isLoadingWorkOrders,
      }}
    >
      {children}
    </WorkOrdersContext.Provider>
  );
}

export function useWorkOrders() {
  const context = useContext(WorkOrdersContext);
  if (!context) {
    throw new Error("useWorkOrders must be used within a WorkOrdersProvider");
  }
  return context;
}
